import React, { useMemo, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowUpDown, CalendarDays, ChevronLeft, ChevronRight,
  CircleDollarSign, Clock, Eye, Info, Megaphone, MessageCircle, RefreshCcw,
  UsersRound,
} from "@/components/icons";
import {
  Area, AreaChart, CartesianGrid, Line, LineChart, PolarAngleAxis, PolarGrid,
  Radar, RadarChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { useStore } from "@/store/store";
import { PageHeader } from "@/components/askilia/layout-bits";
import { BookingStateBadge, StageBadge } from "@/components/askilia/atoms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { money, fmtDay, initials } from "@/lib/format";
import { useI18n } from "@/i18n";
import {
  DEFAULT_INSIGHT_PERIOD, INSIGHT_PERIOD_ORDER, INSIGHT_PERIODS, INSIGHT_WIDGET_DATA,
  LAST_DECISION, SERVICE_CAPACITY_PERIODS,
} from "@/data/insights";

const roundTen = (value) => Math.round(value / 10) * 10;
const percent = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);
const compactMoney = (value) => value >= 1000
  ? `₾${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`
  : money(value);
const signedPercent = (current, previous) => {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const elapsedMinutes = (timestamp) => Math.max(1, Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000));
const elapsedLabel = (minutes, t, formatNumber) => {
  if (minutes < 60) return t("insights.time.minutesShort", { count: formatNumber(minutes) });
  if (minutes < 1440) return t("insights.time.hoursShort", { count: formatNumber(Math.floor(minutes / 60)) });
  return t("insights.time.daysShort", { count: formatNumber(Math.floor(minutes / 1440)) });
};

const EVIDENCE_META = {
  observed: { labelKey: "insights.evidence.observed", dot: "bg-primary" },
  staff: { labelKey: "insights.evidence.entered", dot: "bg-muted-foreground" },
  derived: { labelKey: "insights.evidence.derived", dot: "bg-primary" },
  estimate: { labelKey: "insights.evidence.estimate", dot: "bg-warning" },
  success: { labelKey: "insights.evidence.efficient", dot: "bg-success" },
  active: { labelKey: "insights.evidence.active", dot: "bg-success" },
  info: { labelKey: "insights.evidence.tracked", dot: "bg-primary" },
  neutral: { labelKey: "insights.evidence.context", dot: "bg-muted-foreground" },
  destructive: { labelKey: "insights.evidence.attention", dot: "bg-destructive" },
};

const ACQUISITION_METRICS = {
  spend: {
    labelKey: "insights.acquisition.metric.spend",
    source: "staff",
    detailKey: "insights.acquisition.metric.spendDetail",
    formatKind: "money",
    tone: "human",
  },
  attended: {
    labelKey: "insights.acquisition.metric.attended",
    source: "observed",
    detailKey: "insights.acquisition.metric.attendedDetail",
    formatKind: "number",
    tone: "success",
  },
  costPerAttended: {
    labelKey: "insights.acquisition.metric.costPerAttended",
    source: "derived",
    detailKey: "insights.acquisition.metric.costPerAttendedDetail",
    formatKind: "money",
    tone: "warning",
  },
  campaignReturn: {
    labelKey: "insights.acquisition.metric.return",
    source: "derived",
    detailKey: "insights.acquisition.metric.returnDetail",
    formatKind: "multiple",
    tone: "primary",
  },
};

function formatAcquisitionMetric(metric, value, formatMoney, formatNumber) {
  if (metric.formatKind === "money") return formatMoney(Math.round(value || 0));
  if (metric.formatKind === "number") return formatNumber(Math.round(value || 0));
  return `${formatNumber(Number(value || 0), { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x`;
}

function EvidenceTag({ source, tone, children, className = "" }) {
  const { t, localize } = useI18n();
  const meta = EVIDENCE_META[source || tone] || EVIDENCE_META.neutral;
  return (
    <span className={`inline-flex whitespace-nowrap items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-0.5 text-xs font-medium text-foreground shadow-xs ${className}`}>
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {children ? (typeof children === "string" ? localize(children) : children) : t(meta.labelKey)}
    </span>
  );
}

function getCampaignRows(report, margin) {
  return report.campaigns.map((campaign) => {
    const costPerAttended = campaign.attended ? campaign.spend / campaign.attended : null;
    const valuePerAttended = campaign.attended ? campaign.value / campaign.attended : null;
    const revenueReturn = campaign.spend ? campaign.value / campaign.spend : 0;
    const contribution = margin === null ? null : campaign.value * (margin / 100) - campaign.spend;
    return {
      ...campaign,
      showRate: percent(campaign.attended, campaign.booked),
      messageCost: campaign.enquiries ? campaign.spend / campaign.enquiries : null,
      costPerAttended,
      valuePerAttended,
      revenueReturn,
      contribution,
    };
  });
}

function getOpportunities(report, campaignRows, t, localize, formatMoney, formatNumber) {
  const unrecoveredNoShows = Math.max(0, report.noShows - report.rebooked);
  const best = campaignRows.reduce((winner, row) => row.revenueReturn > winner.revenueReturn ? row : winner, campaignRows[0]);
  const worst = campaignRows.reduce((loser, row) => row.revenueReturn < loser.revenueReturn ? row : loser, campaignRows[0]);
  const actions = [];

  if (report.reallocationBudget > 0 && best && worst) {
    const value = report.reallocationBudget * Math.max(0, best.revenueReturn - worst.revenueReturn);
    actions.push({
      id: "reallocate",
      icon: Megaphone,
      title: t("insights.actions.reallocateTitle", { amount: formatMoney(report.reallocationBudget), from: localize(worst.name), to: localize(best.name) }),
      detail: t("insights.actions.reallocateDetail", { best: formatNumber(best.revenueReturn, { minimumFractionDigits: 1, maximumFractionDigits: 1 }), worst: formatNumber(worst.revenueReturn, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }),
      formula: t("insights.actions.reallocateFormula", { amount: formatMoney(report.reallocationBudget), best: formatNumber(best.revenueReturn, { minimumFractionDigits: 1, maximumFractionDigits: 1 }), worst: formatNumber(worst.revenueReturn, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }),
      value: roundTen(value),
      confidence: t("insights.confidence.low"),
      route: null,
      cta: t("insights.actions.compareCampaigns"),
      campaigns: [best, worst],
    });
  }

  if (report.followupEligible > 0) {
    actions.push({
      id: "followup",
      icon: UsersRound,
      title: t("insights.actions.followupTitle", { count: formatNumber(report.followupEligible) }),
      detail: t("insights.actions.followupDetail", { rate: formatNumber(Math.round(report.followupRate * 100)) }),
      formula: t("insights.actions.followupFormula", { count: formatNumber(report.followupEligible), rate: formatNumber(Math.round(report.followupRate * 100)), value: formatMoney(report.averageFirstVisit) }),
      value: roundTen(report.followupEligible * report.followupRate * report.averageFirstVisit),
      confidence: t("insights.confidence.medium"),
      route: "/customers",
      cta: t("insights.actions.reviewFollowup"),
    });
  }

  if (unrecoveredNoShows > 0) {
    actions.push({
      id: "noshow",
      icon: RefreshCcw,
      title: t("insights.actions.recoveryTitle", { count: formatNumber(unrecoveredNoShows) }),
      detail: t("insights.actions.recoveryDetail", { count: formatNumber(report.rebooked) }),
      formula: t("insights.actions.recoveryFormula", { count: formatNumber(unrecoveredNoShows), value: formatMoney(report.averageFirstVisit) }),
      value: roundTen(unrecoveredNoShows * 0.25 * report.averageFirstVisit),
      confidence: t("insights.confidence.medium"),
      route: "/today",
      cta: t("insights.actions.reviewRecovery"),
    });
  }

  return actions.sort((a, b) => b.value - a.value);
}

export default function Insights() {
  const { state } = useStore();
  const { t, localize, formatMoney, formatNumber } = useI18n();
  const nav = useNavigate();
  const reduceMotion = useReducedMotion();
  const [periodId, setPeriodId] = useState(DEFAULT_INSIGHT_PERIOD);
  const [marginInput, setMarginInput] = useState("");
  const [acquisitionMetric, setAcquisitionMetric] = useState("campaignReturn");
  const [sort, setSort] = useState({ key: "revenueReturn", direction: "desc" });
  const [capacityPeriod, setCapacityPeriod] = useState(DEFAULT_INSIGHT_PERIOD);
  const [capacityPage, setCapacityPage] = useState(0);
  const [activeQueueSort, setActiveQueueSort] = useState("wait");
  const [drill, setDrill] = useState(null);

  const report = INSIGHT_PERIODS[periodId];
  const widgetData = INSIGHT_WIDGET_DATA[periodId];
  const margin = marginInput === "" ? null : Number(marginInput);
  const campaignRows = useMemo(() => getCampaignRows(report, margin), [report, margin]);
  const opportunities = useMemo(
    () => getOpportunities(report, campaignRows, t, localize, formatMoney, formatNumber),
    [report, campaignRows, t, localize, formatMoney, formatNumber]
  );
  const campaignTotals = useMemo(() => campaignRows.reduce((totals, campaign) => ({
    spend: totals.spend + campaign.spend,
    booked: totals.booked + campaign.booked,
    attended: totals.attended + campaign.attended,
    value: totals.value + campaign.value,
  }), { spend: 0, booked: 0, attended: 0, value: 0 }), [campaignRows]);
  const sortedCampaigns = useMemo(() => {
    return [...campaignRows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === "string") return av.localeCompare(bv) * (sort.direction === "asc" ? 1 : -1);
      return ((av ?? -1) - (bv ?? -1)) * (sort.direction === "asc" ? 1 : -1);
    });
  }, [campaignRows, sort]);

  const noShows = report.noShows;
  const showRate = percent(report.attended, report.dueBookings);
  const valueChange = signedPercent(report.visitValue, report.previousVisitValue);
  const trackedReturn = campaignTotals.spend ? campaignTotals.value / campaignTotals.spend : 0;
  const bookingCoverage = percent(campaignTotals.booked, report.bookings);
  const attendanceCoverage = percent(campaignTotals.attended, report.attended);
  const largestOpportunity = opportunities[0]?.value ?? 0;
  const openPipeline = state.customers
    .filter((customer) => ["Qualifying", "Qualified", "Offered"].includes(customer.stage))
    .reduce((sum, customer) => sum + customer.value, 0);
  const staleOpportunities = state.customers.filter(
    (customer) => !customer.nextAction && ["Qualifying", "Qualified", "Offered"].includes(customer.stage)
  );
  const activeBookings = state.bookings.filter((booking) => booking.state !== "cancelled");
  const completedBookings = state.bookings.filter((booking) => booking.state === "completed");
  const noShowBookings = state.bookings.filter((booking) => booking.state === "noshow");
  const breakEvenAcquisition = margin === null ? null : roundTen(report.averageFirstVisit * margin / 100);
  const knownValueCustomers = state.customers.filter((customer) => Number(customer.commercial?.spent || 0) > 0);
  const averageKnownCustomerValue = knownValueCustomers.length
    ? Math.round(knownValueCustomers.reduce((sum, customer) => sum + Number(customer.commercial?.spent || 0), 0) / knownValueCustomers.length)
    : 0;
  const activeCustomerRows = useMemo(() => state.conversations
    .filter((conversation) => conversation.lifecycle !== "resolved" && !conversation.archivedAt)
    .map((conversation) => {
      const customer = state.customers.find((item) => item.id === conversation.customerId);
      const service = state.services.find((item) => item.id === conversation.serviceId);
      const minutes = elapsedMinutes(conversation.lastActivity);
      const status = conversation.waitingOn === "team"
        ? "Operator required"
        : conversation.lifecycle === "human"
          ? "Operator engaged"
          : conversation.lifecycle === "ilia"
            ? "Ilia engaged"
            : conversation.waitingOn === "customer"
              ? "Waiting on customer"
              : "Active";
      return {
        conversationId: conversation.id,
        customerId: conversation.customerId,
        name: customer?.name || t("insights.customer.unknown"),
        service: localize(service?.name || customer?.valueBasis || "General opportunity"),
        channel: localize(conversation.channel || customer?.source || "Unknown channel"),
        customerType: customer?.first ? t("insights.customer.new") : t("insights.customer.returning"),
        status: localize(status),
        statusCode: status,
        waitMinutes: minutes,
        waitLabel: elapsedLabel(minutes, t, formatNumber),
        potentialValue: Number(customer?.commercial?.openValue ?? customer?.value ?? 0),
        urgency: conversation.urgency || "normal",
      };
    }), [state.conversations, state.customers, state.services, t, localize, formatNumber]);

  const selectPeriod = (id) => {
    setPeriodId(id);
    setDrill(null);
  };

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  };

  const openMetric = (key) => {
    const metricMap = {
      value: {
        type: "metric",
        label: t("insights.metric.attendedValue"),
        source: "derived",
        description: t("insights.dialog.valueDescription"),
        records: completedBookings,
      },
      attended: {
        type: "metric",
        label: t("insights.metric.attendedBookings"),
        source: "observed",
        description: t("insights.dialog.attendedDescription"),
        records: completedBookings,
      },
      noshow: {
        type: "metric",
        label: t("insights.metric.noShows"),
        source: "observed",
        description: t("insights.dialog.noShowDescription"),
        records: noShowBookings,
      },
      bookings: {
        type: "metric",
        label: t("insights.metric.bookings"),
        source: "observed",
        description: t("insights.dialog.bookingsDescription"),
        records: activeBookings,
      },
    };
    setDrill(metricMap[key]);
  };

  return (
    <div className="min-h-full bg-background">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:pb-8 lg:pt-10">
        <PageHeader
          title={t("insights.page.title")}
          subtitle={t("insights.page.subtitle")}
          actions={
            <div className="inline-flex flex-wrap items-center rounded-lg border border-border bg-surface p-1 shadow-xs" role="group" aria-label={t("insights.page.reportingPeriod")}>
              {INSIGHT_PERIOD_ORDER.map((id) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={periodId === id}
                  onClick={() => selectPeriod(id)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${periodId === id ? "bg-surface text-foreground shadow-xs ring-1 ring-border" : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"}`}
                >
                  {localize(INSIGHT_PERIODS[id].label)}
                </button>
              ))}
            </div>
          }
        />

        <ReportToolbar report={report} />

        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-12">
          <AttendedValueCard
            report={report}
            valueChange={valueChange}
            reduceMotion={reduceMotion}
            onOpen={() => openMetric("value")}
          />

          <CommercialPulseCard
            report={report}
            showRate={showRate}
            reduceMotion={reduceMotion}
            onOpenMetric={openMetric}
            onOpenInbox={() => nav("/inbox")}
          />

          <JourneyFunnelCard
            report={report}
            showRate={showRate}
            onOpenMetric={openMetric}
            onOpenInbox={() => nav("/inbox")}
          />

          <LeakageRecoveryCard
            report={report}
            opportunities={opportunities}
            onOpenMetric={openMetric}
            onOpenInbox={() => nav("/inbox")}
            onReview={(action) => setDrill({ type: "opportunity", action })}
          />

          <CustomerDemandCard
            report={report}
            widgetData={widgetData}
            averageKnownCustomerValue={averageKnownCustomerValue}
            knownCustomerCount={knownValueCustomers.length}
            reduceMotion={reduceMotion}
          />

          <EnquiryChannelsCard
            report={report}
            channels={widgetData.channels}
            onOpenInbox={() => nav("/inbox")}
          />

          <ServiceCapacityCard
            periodId={capacityPeriod}
            setPeriodId={(id) => { setCapacityPeriod(id); setCapacityPage(0); }}
            page={capacityPage}
            setPage={setCapacityPage}
          />

          <BusyHoursCard
            report={report}
            widgetData={widgetData}
            reduceMotion={reduceMotion}
            onOpenCalendar={() => nav("/calendar")}
          />

          <AcquisitionPerformanceCard
            campaigns={sortedCampaigns}
            totals={campaignTotals}
            report={report}
            trackedReturn={trackedReturn}
            bookingCoverage={bookingCoverage}
            attendanceCoverage={attendanceCoverage}
            selectedMetric={acquisitionMetric}
            setSelectedMetric={setAcquisitionMetric}
            reduceMotion={reduceMotion}
            onOpenCampaign={(campaign) => setDrill({ type: "campaign", campaign })}
          />

          <ActionQueueCard
            opportunities={opportunities}
            largestOpportunity={largestOpportunity}
            onReview={(action) => setDrill({ type: "opportunity", action })}
          />

          <CampaignLeaderboard
            campaigns={sortedCampaigns}
            totals={campaignTotals}
            report={report}
            margin={margin}
            marginInput={marginInput}
            setMarginInput={setMarginInput}
            breakEvenAcquisition={breakEvenAcquisition}
            sort={sort}
            toggleSort={toggleSort}
            onOpenCampaign={(campaign) => setDrill({ type: "campaign", campaign })}
          />

          <div className="xl:col-span-12">
            <DecisionAndContext
              openPipeline={openPipeline}
              staleCount={staleOpportunities.length}
              activeRows={activeCustomerRows}
              activeSort={activeQueueSort}
              setActiveSort={setActiveQueueSort}
              onOpenAttended={() => openMetric("attended")}
              nav={nav}
            />
          </div>
        </div>

        <InsightDialog drill={drill} setDrill={setDrill} state={state} nav={nav} report={report} />
      </div>
    </div>
  );
}

function DashboardCard({ id, title, description, tag, action, className = "", children, footer }) {
  const { localize } = useI18n();
  return (
    <section aria-labelledby={id} className={`min-w-0 overflow-hidden rounded-xl border border-border bg-surface shadow-xs ${className}`}>
      <div className="flex min-h-[72px] flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id={id} className="text-base font-semibold text-foreground">{typeof title === "string" ? localize(title) : title}</h2>
            {tag}
          </div>
          {description ? <p className="mt-1 text-xs leading-5 text-muted-foreground">{typeof description === "string" ? localize(description) : description}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
      {footer ? <div className="border-t border-border bg-muted/25 px-5 py-3 sm:px-6">{footer}</div> : null}
    </section>
  );
}

function AttendedValueCard({ report, valueChange, reduceMotion, onOpen }) {
  const { t, localize, formatMoney, formatNumber } = useI18n();
  const trend = report.trend.map((point) => ({ ...point, label: localize(point.label) }));
  return (
    <DashboardCard
      id="attended-value-card"
      title={t("insights.attended.title")}
      description={t("insights.attended.description")}
      tag={<EvidenceTag source="derived" />}
      className="xl:col-span-8"
      action={
        <button type="button" onClick={onOpen} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {t("insights.action.viewRecords")} <ChevronRight className="h-4 w-4" />
        </button>
      }
      footer={
        <div className="grid grid-cols-2 gap-4 text-xs sm:grid-cols-3">
          <MetricSummary label={t("insights.metric.attendedVisits")} value={formatNumber(report.attended)} detail={t("insights.metric.appointmentsDue", { count: formatNumber(report.dueBookings) })} />
          <MetricSummary label={t("insights.metric.averageFirstVisit")} value={formatMoney(report.averageFirstVisit)} detail={t("insights.metric.importedPriceBasis")} />
          <MetricSummary className="col-span-2 sm:col-span-1" label={t("insights.metric.comparison")} value={formatMoney(report.previousVisitValue)} detail={localize(report.comparison)} />
        </div>
      }
    >
      <div className="px-5 pb-5 pt-5 sm:px-6 sm:pb-6">
        <div className="flex flex-wrap items-end gap-3">
          <p className="text-[36px] font-semibold leading-none tracking-[-0.04em] text-foreground sm:text-[40px]">{formatMoney(report.visitValue)}</p>
          {valueChange !== null ? (
            <EvidenceTag tone={valueChange >= 0 ? "success" : "destructive"} className="mb-0.5">
              {t("insights.metric.vsComparison", { value: `${valueChange >= 0 ? "+" : ""}${formatNumber(valueChange)}%` })}
            </EvidenceTag>
          ) : null}
        </div>
        <figure className="relative mt-6 min-w-0">
          <figcaption className="sr-only">{t("insights.chart.attendedValueCaption", { comparison: localize(report.comparison) })}</figcaption>
          <div className="h-[220px] min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 760, height: 220 }}>
              <AreaChart accessibilityLayer data={trend} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`dashboardValueFill-${report.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.65} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} dy={9} />
                <YAxis hide domain={[0, "auto"]} />
                <Tooltip isAnimationActive={!reduceMotion} content={<ValueChartTooltip comparison={localize(report.comparison)} />} cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "4 4" }} />
                <Area type="monotone" dataKey="previous" name={t("insights.chart.comparison")} stroke="hsl(var(--muted-foreground))" strokeWidth={1.75} strokeDasharray="5 5" fillOpacity={0} isAnimationActive={false} />
                <Area key={`${report.id}-dashboard-current`} type="monotone" dataKey="current" name={t("insights.chart.thisPeriod")} stroke="hsl(var(--primary))" strokeWidth={2.5} fill={`url(#dashboardValueFill-${report.id})`} isAnimationActive={!reduceMotion} animationDuration={650} animationEasing="ease-out" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2"><span className="h-0.5 w-5 bg-primary" />{t("insights.chart.thisPeriod")}</span>
            <span className="inline-flex items-center gap-2"><span className="h-0.5 w-5 border-t-2 border-dashed border-muted-foreground/70" />{t("insights.chart.comparison")}</span>
          </div>
        </figure>
      </div>
    </DashboardCard>
  );
}

function MetricSummary({ label, value, detail, className = "" }) {
  const { localize } = useI18n();
  return (
    <div className={className}>
      <p className="font-medium text-muted-foreground">{typeof label === "string" ? localize(label) : label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</p>
      <p className="mt-0.5 break-words text-muted-foreground">{typeof detail === "string" ? localize(detail) : detail}</p>
    </div>
  );
}

function CommercialPulseCard({ report, showRate, reduceMotion, onOpenMetric, onOpenInbox }) {
  const { t, formatNumber } = useI18n();
  const bookingRate = percent(report.bookings, report.enquiries);
  const attendedRate = percent(report.attended, report.enquiries);
  const pulseTrend = (report.metricTrend || []).map((point) => ({
    ...point,
    attendedRate: (point.showRate * point.bookingRate) / 100,
  }));
  return (
    <DashboardCard
      id="commercial-pulse-card"
      title={t("insights.pulse.title")}
      description={t("insights.pulse.description")}
      tag={<EvidenceTag source="observed" />}
      className="xl:col-span-4"
    >
      <div className="divide-y divide-border">
        <PulseRow label={t("insights.metric.showRate")} value={`${formatNumber(showRate)}%`} detail={t("insights.metric.ofDue", { attended: formatNumber(report.attended), due: formatNumber(report.dueBookings) })} data={pulseTrend} dataKey="showRate" tone="success" reduceMotion={reduceMotion} onClick={() => onOpenMetric("attended")} />
        <PulseRow label={t("insights.metric.enquiryToBooking")} value={`${formatNumber(bookingRate)}%`} detail={t("insights.metric.bookingsFromEnquiries", { bookings: formatNumber(report.bookings), enquiries: formatNumber(report.enquiries) })} data={pulseTrend} dataKey="bookingRate" tone="primary" reduceMotion={reduceMotion} onClick={onOpenInbox} />
        <PulseRow label={t("insights.metric.enquiryToAttended")} value={`${formatNumber(attendedRate)}%`} detail={t("insights.metric.attendedOutcomes", { count: formatNumber(report.attended) })} data={pulseTrend} dataKey="attendedRate" tone="primary" reduceMotion={reduceMotion} onClick={() => onOpenMetric("attended")} />
      </div>
    </DashboardCard>
  );
}

function PulseRow({ label, value, detail, data, dataKey, tone, reduceMotion, onClick }) {
  const { localize } = useI18n();
  return (
    <button type="button" onClick={onClick} className="grid w-full grid-cols-[minmax(0,1fr)_112px] items-center gap-4 px-5 py-5 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-6">
      <div className="min-w-0">
        <p className="text-xs font-medium text-muted-foreground">{localize(label)}</p>
        <p className="mt-1 text-2xl font-semibold tracking-[-0.03em] text-foreground">{value}</p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{detail}</p>
      </div>
      <CompactSparkline data={data} dataKey={dataKey} tone={tone} reduceMotion={reduceMotion} />
    </button>
  );
}

function CompactSparkline({ data, dataKey, tone = "primary", reduceMotion }) {
  return (
    <div aria-hidden="true" className="h-12 min-w-0">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 112, height: 48 }}>
        <LineChart data={data} accessibilityLayer={false} margin={{ top: 5, right: 2, bottom: 3, left: 2 }}>
          <Line type="monotone" dataKey={dataKey} dot={false} activeDot={false} stroke={`hsl(var(--${tone}))`} strokeWidth={2.1} isAnimationActive={!reduceMotion} animationDuration={480} animationEasing="ease-out" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function JourneyFunnelCard({ report, showRate, onOpenMetric, onOpenInbox }) {
  const { t, localize, formatNumber } = useI18n();
  const bookingRate = percent(report.bookings, report.enquiries);
  const attendedRate = percent(report.attended, report.enquiries);
  const stages = [
    { label: "Enquiries", value: report.enquiries, share: 100, detail: t("insights.journey.demandCaptured"), onClick: onOpenInbox },
    { label: "Bookings", value: report.bookings, share: bookingRate, detail: t("insights.journey.didNotBook", { count: formatNumber(report.enquiries - report.bookings) }), onClick: () => onOpenMetric("bookings") },
    { label: "Attended", value: report.attended, share: attendedRate, detail: t("insights.journey.showRate", { value: formatNumber(showRate) }), onClick: () => onOpenMetric("attended") },
  ];
  return (
    <DashboardCard
      id="journey-funnel-card"
      title={t("insights.journey.title")}
      description={t("insights.journey.description")}
      tag={<EvidenceTag source="derived" />}
      className="xl:col-span-5"
      footer={<p className="text-xs leading-5 text-muted-foreground">{t("insights.journey.recoveryNote")}</p>}
    >
      <div className="space-y-5 p-5 sm:p-6">
        {stages.map((stage) => (
          <button key={stage.label} type="button" onClick={stage.onClick} className="group block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <span className="flex items-end justify-between gap-4">
              <span>
                <span className="block text-sm font-semibold text-foreground">{localize(stage.label)}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{stage.detail}</span>
              </span>
              <span className="text-2xl font-semibold tabular-nums text-foreground">{formatNumber(stage.value)}</span>
            </span>
            <span className="mt-2.5 block h-2 overflow-hidden rounded-full bg-muted">
              <span className="block h-full rounded-full bg-primary transition-[width] duration-500 group-hover:bg-primary/85" style={{ width: `${Math.max(6, stage.share)}%` }} />
            </span>
          </button>
        ))}
        <div className="grid grid-cols-2 gap-3 border-t border-border pt-5">
          <MetricSummary label={t("insights.metric.noShows")} value={formatNumber(report.noShows)} detail={t("insights.metric.percentDueVisits", { value: formatNumber(percent(report.noShows, report.dueBookings)) })} />
          <MetricSummary label={t("insights.metric.rebooked")} value={formatNumber(report.rebooked)} detail={t("insights.metric.percentRecovery", { value: formatNumber(percent(report.rebooked, report.noShows)) })} />
        </div>
      </div>
    </DashboardCard>
  );
}

function LeakageRecoveryCard({ report, opportunities, onOpenMetric, onOpenInbox, onReview }) {
  const { t, formatMoney, formatNumber } = useI18n();
  const unbooked = Math.max(0, report.enquiries - report.bookings);
  const unrecovered = Math.max(0, report.noShows - report.rebooked);
  const followupAction = opportunities.find((action) => action.id === "followup");
  const recoveryAction = opportunities.find((action) => action.id === "noshow");
  return (
    <DashboardCard
      id="leakage-recovery-card"
      title={t("insights.recovery.title")}
      description={t("insights.recovery.description")}
      tag={<EvidenceTag source="observed" />}
      className="xl:col-span-7"
    >
      <div className="grid min-h-[310px] gap-0 md:grid-cols-[minmax(0,1fr)_minmax(250px,0.75fr)]">
        <div className="grid grid-cols-2 gap-px bg-border">
          <LeakageMetric label={t("insights.metric.enquiriesWithoutBooking")} value={formatNumber(unbooked)} detail={t("insights.metric.percentOfEnquiries", { value: formatNumber(percent(unbooked, report.enquiries)) })} onClick={onOpenInbox} />
          <LeakageMetric label={t("insights.metric.noShows")} value={formatNumber(report.noShows)} detail={t("insights.metric.percentDueVisits", { value: formatNumber(percent(report.noShows, report.dueBookings)) })} onClick={() => onOpenMetric("noshow")} />
          <LeakageMetric label={t("insights.metric.unrecoveredNoShows")} value={formatNumber(unrecovered)} detail={t("insights.recovery.alreadyRebooked", { count: formatNumber(report.rebooked) })} onClick={() => onOpenMetric("noshow")} />
          <LeakageMetric label={t("insights.metric.recoveryRate")} value={`${formatNumber(percent(report.rebooked, report.noShows))}%`} detail={t("insights.recovery.observedRebookings")} onClick={() => onOpenMetric("noshow")} />
        </div>
        <div className="border-t border-border bg-warning-soft/45 p-5 md:border-l md:border-t-0 sm:p-6">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-foreground">{t("insights.recovery.modeled")}</p>
            <EvidenceTag source="estimate" />
          </div>
          <p className="mt-5 text-3xl font-semibold tracking-[-0.03em] text-foreground">{t("insights.value.about", { value: formatMoney(recoveryAction?.value || 0) })}</p>
          <p className="mt-2 text-sm leading-5 text-muted-foreground">{t("insights.recovery.scenario")}</p>
          <div className="mt-5 flex flex-col gap-2">
            {recoveryAction ? <Button variant="outline" size="sm" onClick={() => onReview(recoveryAction)}>{t("insights.actions.reviewNoShow")} <ArrowRight className="h-3.5 w-3.5" /></Button> : null}
            {followupAction ? <button type="button" onClick={() => onReview(followupAction)} className="text-left text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{t("insights.actions.reviewCandidates", { count: formatNumber(report.followupEligible) })}</button> : null}
          </div>
        </div>
      </div>
    </DashboardCard>
  );
}

function LeakageMetric({ label, value, detail, onClick }) {
  const { localize } = useI18n();
  return (
    <button type="button" onClick={onClick} className="min-h-[150px] bg-surface p-5 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:p-6">
      <p className="text-xs font-medium leading-5 text-muted-foreground">{localize(label)}</p>
      <p className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-foreground">{value}</p>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">{typeof detail === "string" ? localize(detail) : detail}</p>
    </button>
  );
}

function CustomerDemandCard({ report, widgetData, averageKnownCustomerValue, knownCustomerCount, reduceMotion }) {
  const { t, localize, formatMoney, formatNumber } = useI18n();
  const averageAttendedValue = report.attended ? Math.round(report.visitValue / report.attended) : 0;
  const { cohorts } = widgetData;
  const radarData = cohorts.services.map((service) => ({
    ...service,
    service: localize(service.service),
    newShare: percent(service.newCustomers, cohorts.newCount),
    returningShare: percent(service.returningCustomers, cohorts.returningCount),
  }));
  const valueTrend = widgetData.averageValueTrend.map((value, index) => ({ index, value }));

  return (
    <DashboardCard
      id="customer-demand-card"
      title={t("insights.demand.title")}
      description={t("insights.demand.description")}
      tag={<EvidenceTag source="derived" />}
      className="xl:col-span-7"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{t("insights.demand.normalized")}</span>
          <span>{t("insights.demand.classified", { count: formatNumber(cohorts.newCount + cohorts.returningCount) })}</span>
        </div>
      }
    >
      <div className="grid min-h-[360px] gap-0 lg:grid-cols-[220px_minmax(0,1fr)]">
        <div className="border-b border-border p-5 sm:p-6 lg:border-b-0 lg:border-r">
          <p className="text-xs font-medium text-muted-foreground">{t("insights.demand.averageVisit")}</p>
          <p className="mt-2 text-[36px] font-semibold leading-none tracking-[-0.04em] text-foreground">{formatMoney(averageAttendedValue)}</p>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">{t("insights.demand.averageVisitDetail")}</p>
          <div className="mt-5 h-16 min-w-0" aria-hidden="true">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 180, height: 64 }}>
              <AreaChart data={valueTrend} margin={{ top: 5, right: 2, bottom: 2, left: 2 }}>
                <defs>
                  <linearGradient id={`averageValueFill-${report.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.16} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2.2} fill={`url(#averageValueFill-${report.id})`} isAnimationActive={!reduceMotion} animationDuration={520} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-5 border-t border-border pt-4">
            <p className="text-xs font-medium text-muted-foreground">{t("insights.demand.returningValue")}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">{formatMoney(averageKnownCustomerValue)}</p>
            <p className="mt-1 text-[11px] leading-4 text-muted-foreground">{t("insights.demand.returningValueDetail", { count: formatNumber(knownCustomerCount) })}</p>
          </div>
        </div>

        <figure className="min-w-0 p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <figcaption className="text-sm font-semibold text-foreground">{t("insights.demand.byService")}</figcaption>
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" />{t("insights.customer.newCount", { count: formatNumber(cohorts.newCount) })}</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-muted-foreground" />{t("insights.customer.returningCount", { count: formatNumber(cohorts.returningCount) })}</span>
            </div>
          </div>
          <div className="mt-2 h-[275px] min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 430, height: 275 }}>
              <RadarChart data={radarData} outerRadius="68%" margin={{ top: 18, right: 30, bottom: 18, left: 30 }}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis dataKey="service" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} />
                <Tooltip isAnimationActive={!reduceMotion} content={<DemandRadarTooltip />} />
                <Radar name={t("insights.customer.newPlural")} dataKey="newShare" stroke="hsl(var(--primary))" strokeWidth={2} fill="hsl(var(--primary))" fillOpacity={0.10} isAnimationActive={!reduceMotion} animationDuration={580} />
                <Radar name={t("insights.customer.returningPlural")} dataKey="returningShare" stroke="hsl(var(--muted-foreground))" strokeWidth={2} fill="hsl(var(--muted-foreground))" fillOpacity={0.07} isAnimationActive={!reduceMotion} animationDuration={580} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </figure>
      </div>
    </DashboardCard>
  );
}

function DemandRadarTooltip({ active, payload, label }) {
  const { t, localize, formatNumber } = useI18n();
  if (!active || !payload?.length) return null;
  const service = localize(label || payload[0]?.payload?.service) || t("insights.service.fallback");
  return (
    <div className="rounded-lg border border-border bg-surface p-3 text-xs shadow-pop">
      <p className="font-semibold text-foreground">{service}</p>
      {payload.map((item) => (
        <p key={item.dataKey} className="mt-1 flex items-center justify-between gap-5 text-muted-foreground">
          <span>{localize(item.name)}</span><span className="font-semibold tabular-nums text-foreground">{formatNumber(Math.round(item.value))}%</span>
        </p>
      ))}
    </div>
  );
}

function EnquiryChannelsCard({ report, channels, onOpenInbox }) {
  const { t, localize, formatNumber } = useI18n();
  const total = channels.reduce((sum, channel) => sum + channel.enquiries, 0);
  const toneClasses = ["bg-primary", "bg-primary/75", "bg-primary/50", "bg-muted-foreground/35"];
  return (
    <DashboardCard
      id="enquiry-channels-card"
      title={t("insights.channels.title")}
      description={t("insights.channels.description")}
      tag={<EvidenceTag source="observed" />}
      className="xl:col-span-5"
      action={<button type="button" onClick={onOpenInbox} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{t("insights.action.openInbox")} <ChevronRight className="h-4 w-4" /></button>}
      footer={<p className="text-xs text-muted-foreground">{t("insights.channels.coverage", { covered: formatNumber(total), total: formatNumber(report.enquiries) })}</p>}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-end justify-between gap-4">
          <div><p className="text-xs font-medium text-muted-foreground">{t("insights.channels.trackedEnquiries")}</p><p className="mt-1 text-3xl font-semibold tracking-[-0.03em] text-foreground">{formatNumber(total)}</p></div>
          <p className="text-right text-xs text-muted-foreground">{t("insights.channels.bookingSummary", { count: formatNumber(report.bookings), rate: formatNumber(percent(report.bookings, total)) })}</p>
        </div>
        <div className="mt-5 flex h-2.5 overflow-hidden rounded-full bg-muted" aria-label={t("insights.channels.shareAria")}>
          {channels.map((channel, index) => <span key={channel.channel} className={`${toneClasses[index]} transition-[width] duration-500`} style={{ width: `${percent(channel.enquiries, total)}%` }} />)}
        </div>
        <div className="mt-6 divide-y divide-border">
          {channels.map((channel, index) => (
            <button key={channel.channel} type="button" onClick={onOpenInbox} className="grid w-full grid-cols-[minmax(0,1fr)_58px_82px] items-center gap-3 py-3 text-left transition-colors hover:bg-muted/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
              <span className="flex min-w-0 items-center gap-2.5"><span className={`h-2.5 w-2.5 shrink-0 rounded-full ${toneClasses[index]}`} /><span className="truncate text-sm font-medium text-foreground">{localize(channel.channel)}</span></span>
              <span className="text-right text-xs tabular-nums text-muted-foreground">{formatNumber(percent(channel.enquiries, total))}%</span>
              <span className="text-right"><span className="block text-sm font-semibold tabular-nums text-foreground">{formatNumber(channel.enquiries)}</span><span className="text-[11px] text-muted-foreground">{t("insights.channels.bookedRate", { value: formatNumber(percent(channel.bookings, channel.enquiries)) })}</span></span>
            </button>
          ))}
        </div>
      </div>
    </DashboardCard>
  );
}

function ServiceCapacityCard({ periodId, setPeriodId, page, setPage }) {
  const { t, localize, formatNumber } = useI18n();
  const rows = SERVICE_CAPACITY_PERIODS[periodId]
    .map((service) => ({ ...service, used: percent(service.booked, service.slots) }))
    .sort((a, b) => b.used - a.used);
  const pageSize = 3;
  const pageCount = Math.ceil(rows.length / pageSize);
  const visibleRows = rows.slice(page * pageSize, page * pageSize + pageSize);
  return (
    <DashboardCard
      id="service-capacity-card"
      title={t("insights.capacity.title")}
      description={t("insights.capacity.description")}
      tag={<EvidenceTag source="derived" />}
      className="xl:col-span-7"
      action={
        <div className="inline-flex flex-wrap rounded-lg border border-border bg-muted/35 p-1" role="group" aria-label={t("insights.capacity.periodAria")}>
          {INSIGHT_PERIOD_ORDER.map((id) => <button key={id} type="button" aria-pressed={periodId === id} onClick={() => setPeriodId(id)} className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${periodId === id ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>{localize(INSIGHT_PERIODS[id].label)}</button>)}
        </div>
      }
      footer={<p className="text-xs text-muted-foreground">{t("insights.capacity.overlapNote")}</p>}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between gap-4">
          <div><p className="text-xs font-medium text-muted-foreground">{t("insights.capacity.highestPressure")}</p><p className="mt-1 text-2xl font-semibold text-foreground">{localize(rows[0].service)} <span className="text-primary">{formatNumber(rows[0].used)}%</span></p></div>
          <div className="flex items-center gap-2">
            <button type="button" disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground shadow-xs transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-35" aria-label={t("insights.capacity.previous")}><ChevronLeft className="h-4 w-4" /></button>
            <span className="min-w-20 text-center text-xs text-muted-foreground">{t("insights.pagination.range", { start: formatNumber(page * pageSize + 1), end: formatNumber(Math.min(rows.length, page * pageSize + pageSize)), total: formatNumber(rows.length) })}</span>
            <button type="button" disabled={page >= pageCount - 1} onClick={() => setPage(Math.min(pageCount - 1, page + 1))} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground shadow-xs transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-35" aria-label={t("insights.capacity.next")}><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="mt-6 space-y-5">
          {visibleRows.map((service) => (
            <div key={service.id}>
              <div className="flex items-end justify-between gap-4">
                <div><p className="text-sm font-semibold text-foreground">{localize(service.service)}</p><p className="mt-0.5 text-xs text-muted-foreground">{t("insights.capacity.bookedSlots", { booked: formatNumber(service.booked), slots: formatNumber(service.slots) })}</p></div>
                <p className={`text-lg font-semibold tabular-nums ${service.used >= 85 ? "text-warning" : "text-foreground"}`}>{formatNumber(service.used)}%</p>
              </div>
              <div className="mt-2.5 grid grid-cols-[repeat(20,minmax(0,1fr))] gap-1" aria-label={t("insights.capacity.usedAria", { service: localize(service.service), value: formatNumber(service.used) })}>
                {Array.from({ length: 20 }).map((_, index) => <span key={index} className={`h-8 rounded-sm transition-colors duration-300 ${index < Math.ceil(service.used / 5) ? (service.used >= 85 ? "bg-warning" : "bg-primary") : "bg-muted"}`} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardCard>
  );
}

function BusyHoursCard({ report, widgetData, reduceMotion, onOpenCalendar }) {
  const { t, localize, formatNumber } = useI18n();
  const peak = widgetData.busyHours.reduce((winner, row) => row.starts > winner.starts ? row : winner, widgetData.busyHours[0]);
  const isDay = report.id === "day";
  return (
    <DashboardCard
      id="busy-hours-card"
      title={isDay ? t("insights.busy.titleDay") : t("insights.busy.titleAverage")}
      description={t("insights.busy.description")}
      tag={<EvidenceTag source="derived" />}
      className="xl:col-span-5"
      action={<button type="button" onClick={onOpenCalendar} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{t("insights.action.calendar")} <ChevronRight className="h-4 w-4" /></button>}
      footer={<p className="text-xs text-muted-foreground">{isDay ? t("insights.busy.cutoffNote") : t("insights.busy.averageNote", { count: formatNumber(widgetData.eligibleDays) })}</p>}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary-soft text-primary-soft-foreground"><Clock className="h-5 w-5" /></span>
          <div><p className="text-xs font-medium text-muted-foreground">{t("insights.busy.peakTime")}</p><p className="mt-0.5 text-2xl font-semibold text-foreground">{peak.hour} <span className="text-sm font-medium text-muted-foreground">{isDay ? t("insights.busy.bookings", { count: formatNumber(peak.starts) }) : t("insights.busy.perOpenDay", { count: formatNumber(peak.starts, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}</span></p></div>
        </div>
        <figure className="relative mt-6 min-w-0">
          <figcaption className="sr-only">{t("insights.busy.caption", { range: localize(report.range) })}</figcaption>
          <div className="h-[220px] min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 420, height: 220 }}>
              <AreaChart data={widgetData.busyHours} margin={{ top: 8, right: 22, bottom: 0, left: 22 }}>
                <defs>
                  <linearGradient id={`busyHoursFill-${report.id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.15} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="4 5" />
                <XAxis dataKey="hour" interval={1} tickLine={false} axisLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} dy={8} />
                <YAxis hide domain={[0, "auto"]} />
                <Tooltip isAnimationActive={!reduceMotion} content={<BusyHoursTooltip isDay={isDay} eligibleDays={widgetData.eligibleDays} />} cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "4 4" }} />
                <Area type="stepAfter" dataKey="starts" stroke="hsl(var(--primary))" strokeWidth={2.4} fill={`url(#busyHoursFill-${report.id})`} isAnimationActive={!reduceMotion} animationDuration={580} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </figure>
      </div>
    </DashboardCard>
  );
}

function BusyHoursTooltip({ active, payload, label, isDay, eligibleDays }) {
  const { t, formatNumber } = useI18n();
  if (!active || !payload?.length) return null;
  const starts = Number(payload[0].value || 0);
  return (
    <div className="rounded-lg border border-border bg-surface p-3 text-xs shadow-pop">
      <p className="font-semibold text-foreground">{label}</p>
      <p className="mt-1 text-muted-foreground">{isDay ? t("insights.busy.starts", { count: formatNumber(starts) }) : t("insights.busy.startsPerOpenDay", { count: formatNumber(starts, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}</p>
      {!isDay ? <p className="mt-1 text-muted-foreground">{t("insights.busy.acrossDays", { count: formatNumber(eligibleDays) })}</p> : null}
    </div>
  );
}

function getChannelRows(campaigns) {
  const grouped = campaigns.reduce((result, campaign) => {
    const current = result[campaign.channel] || { channel: campaign.channel, spend: 0, attended: 0, value: 0, campaigns: [] };
    current.spend += campaign.spend;
    current.attended += campaign.attended;
    current.value += campaign.value;
    current.campaigns.push(campaign);
    result[campaign.channel] = current;
    return result;
  }, {});
  return Object.values(grouped).map((row) => ({
    ...row,
    return: row.spend ? row.value / row.spend : 0,
    costPerAttended: row.attended ? row.spend / row.attended : null,
    bestCampaign: row.campaigns.reduce((best, campaign) => campaign.revenueReturn > best.revenueReturn ? campaign : best, row.campaigns[0]),
  })).sort((a, b) => b.return - a.return);
}

function AcquisitionPerformanceCard({ campaigns, totals, report, trackedReturn, bookingCoverage, attendanceCoverage, selectedMetric, setSelectedMetric, reduceMotion, onOpenCampaign }) {
  const { t, localize, formatMoney, formatNumber } = useI18n();
  const costPerAttended = totals.attended ? totals.spend / totals.attended : 0;
  const values = { spend: totals.spend, attended: totals.attended, costPerAttended, campaignReturn: trackedReturn };
  const metric = ACQUISITION_METRICS[selectedMetric];
  const channelRows = getChannelRows(campaigns);
  const maxReturn = Math.max(1, ...channelRows.map((channel) => channel.return));
  return (
    <DashboardCard
      id="acquisition-performance-card"
      title={t("insights.acquisition.title")}
      description={t("insights.acquisition.description")}
      tag={<EvidenceTag source={metric.source} />}
      className="xl:col-span-8"
      footer={
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <EvidenceTag tone={bookingCoverage >= 80 ? "success" : "estimate"}>{t("insights.acquisition.bookingAttribution", { value: formatNumber(bookingCoverage) })}</EvidenceTag>
          <EvidenceTag tone={attendanceCoverage >= 80 ? "success" : "estimate"}>{t("insights.acquisition.attendanceAttribution", { value: formatNumber(attendanceCoverage) })}</EvidenceTag>
          <span>{t("insights.acquisition.partialNote")}</span>
        </div>
      }
    >
      <div className="p-5 sm:p-6">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {Object.entries(ACQUISITION_METRICS).map(([key, item]) => (
            <button key={key} type="button" aria-pressed={selectedMetric === key} onClick={() => setSelectedMetric(key)} className={`rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selectedMetric === key ? "border-primary bg-primary-soft" : "border-border bg-surface hover:bg-muted/35"}`}>
              <span className="block text-[11px] font-medium leading-4 text-muted-foreground">{t(item.labelKey)}</span>
              <span className="mt-1 block text-lg font-semibold tabular-nums text-foreground">{formatAcquisitionMetric(item, values[key], formatMoney, formatNumber)}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(220px,0.7fr)]">
          <figure className="relative min-w-0">
            <figcaption className="sr-only">{t("insights.acquisition.trendCaption", { metric: t(metric.labelKey), range: localize(report.range) })}</figcaption>
            <div className="h-[210px] min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 540, height: 210 }}>
                <AreaChart accessibilityLayer data={report.acquisitionTrend} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id={`dashboardAcquisitionFill-${report.id}-${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={`hsl(var(--${metric.tone}))`} stopOpacity={0.15} />
                      <stop offset="100%" stopColor={`hsl(var(--${metric.tone}))`} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.65} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} dy={9} />
                  <YAxis hide domain={[0, "auto"]} />
                  <Tooltip isAnimationActive={!reduceMotion} content={<AcquisitionTooltip metric={metric} />} cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "4 4" }} />
                  <Area key={`${report.id}-${selectedMetric}-dashboard`} type="monotone" dataKey={selectedMetric} stroke={`hsl(var(--${metric.tone}))`} strokeWidth={2.4} fill={`url(#dashboardAcquisitionFill-${report.id}-${selectedMetric})`} isAnimationActive={!reduceMotion} animationDuration={580} animationEasing="ease-out" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </figure>

          <div className="space-y-5 border-t border-border pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <div>
              <p className="text-xs font-medium text-muted-foreground">{t("insights.acquisition.currentMetric")}</p>
              <p className="mt-1 text-3xl font-semibold tracking-[-0.03em] text-foreground">{formatAcquisitionMetric(metric, values[selectedMetric], formatMoney, formatNumber)}</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{t(metric.detailKey)}</p>
            </div>
            {channelRows.map((channel) => (
              <button key={channel.channel} type="button" onClick={() => onOpenCampaign(channel.bestCampaign)} className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-semibold text-foreground">{localize(channel.channel)}</span>
                  <span className="font-semibold tabular-nums text-foreground">{formatNumber(channel.return, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x</span>
                </span>
                <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-muted"><span className="block h-full rounded-full bg-primary" style={{ width: `${Math.max(8, (channel.return / maxReturn) * 100)}%` }} /></span>
                <span className="mt-1.5 flex items-center justify-between gap-3 text-[11px] text-muted-foreground"><span>{t("insights.acquisition.spendValue", { value: formatMoney(channel.spend) })}</span><span>{t("insights.acquisition.attendedValue", { count: formatNumber(channel.attended) })}</span></span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </DashboardCard>
  );
}

function ActionQueueCard({ opportunities, largestOpportunity, onReview }) {
  const { t, formatMoney } = useI18n();
  return (
    <DashboardCard
      id="action-queue-card"
      title={t("insights.actions.title")}
      description={t("insights.actions.description")}
      tag={<EvidenceTag source="estimate" />}
      className="xl:col-span-4"
      footer={<p className="text-xs text-muted-foreground">{t("insights.actions.overlapNote")}</p>}
    >
      <div className="divide-y divide-border">
        {opportunities.map((action) => {
          const Icon = action.icon;
          return (
            <button key={action.id} type="button" onClick={() => onReview(action)} className="grid w-full grid-cols-[36px_minmax(0,1fr)_auto] gap-3 px-5 py-4 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-6">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/45 text-muted-foreground"><Icon className="h-4 w-4" /></span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold leading-5 text-foreground">{action.title}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{action.confidence}</span>
              </span>
              <span className="text-right">
                <span className="block text-[11px] text-muted-foreground">{t("insights.actions.possible")}</span>
                <span className="mt-1 block text-sm font-semibold tabular-nums text-foreground">{formatMoney(action.value)}</span>
                <ChevronRight className="ml-auto mt-1 h-4 w-4 text-muted-foreground" />
              </span>
            </button>
          );
        })}
      </div>
      <div className="border-t border-border bg-warning-soft/35 px-5 py-3 sm:px-6">
        <p className="text-xs text-muted-foreground">{t("insights.actions.largestEstimate")} <span className="font-semibold text-foreground">{t("insights.value.about", { value: formatMoney(largestOpportunity) })}</span></p>
      </div>
    </DashboardCard>
  );
}

function CampaignLeaderboard({ campaigns, totals, report, margin, marginInput, setMarginInput, breakEvenAcquisition, sort, toggleSort, onOpenCampaign }) {
  const { t, localize, formatMoney, formatNumber } = useI18n();
  const bestReturn = Math.max(...campaigns.map((campaign) => campaign.revenueReturn));
  return (
    <DashboardCard
      id="campaign-leaderboard-card"
      title={t("insights.campaigns.title")}
      description={t("insights.campaigns.description")}
      tag={<EvidenceTag tone="info">{t("insights.campaigns.attributedOutcomes")}</EvidenceTag>}
      className="xl:col-span-12"
      action={
        <details className="group sm:text-right">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-sm font-semibold text-foreground shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {t("insights.campaigns.modelContribution")} <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
          </summary>
          <div className="mt-3 flex items-center gap-2 sm:justify-end">
            <label htmlFor="dashboard-gross-margin" className="text-xs text-muted-foreground">{t("insights.campaigns.grossMargin")}</label>
            <Input id="dashboard-gross-margin" type="number" min="1" max="100" inputMode="numeric" value={marginInput} onChange={(event) => setMarginInput(event.target.value === "" ? "" : String(Math.min(100, Math.max(1, Number(event.target.value)))))} placeholder="40" className="h-9 w-20 bg-surface text-right tabular-nums" />
            <span className="text-sm font-medium text-foreground">%</span>
          </div>
          <p className="mt-2 max-w-sm text-left text-xs leading-5 text-muted-foreground sm:text-right">
            {breakEvenAcquisition === null ? t("insights.campaigns.optionalAssumption") : t("insights.campaigns.breakEven", { margin: formatNumber(margin), value: formatMoney(breakEvenAcquisition) })}
          </p>
        </details>
      }
      footer={
        <div className="flex flex-col gap-1 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span>{t("insights.campaigns.attributedBookings", { attributed: formatNumber(totals.booked), total: formatNumber(report.bookings) })}</span>
          <span>{t("insights.campaigns.messageCostNote")}</span>
        </div>
      }
    >
      <div className="divide-y divide-border lg:hidden">
        {campaigns.map((campaign) => <CampaignEvidenceCard key={campaign.id} campaign={campaign} margin={margin} bestReturn={bestReturn} onOpen={() => onOpenCampaign(campaign)} />)}
      </div>
      <div className="hidden overflow-x-auto scroll-thin lg:block">
        <Table className="min-w-[940px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortableHead label={t("insights.campaigns.campaign")} sortKey="name" sort={sort} onSort={toggleSort} />
              <SortableHead label={t("insights.campaigns.spend")} sortKey="spend" sort={sort} onSort={toggleSort} align="right" />
              <SortableHead label={t("insights.campaigns.attendedResult")} sortKey="showRate" sort={sort} onSort={toggleSort} align="right" />
              <SortableHead label={t("insights.campaigns.costPerAttended")} sortKey="costPerAttended" sort={sort} onSort={toggleSort} align="right" />
              <SortableHead label={t("insights.campaigns.attendedValue")} sortKey="value" sort={sort} onSort={toggleSort} align="right" />
              <SortableHead label={t("insights.campaigns.valuePerLari")} sortKey="revenueReturn" sort={sort} onSort={toggleSort} align="right" />
              {margin !== null ? <SortableHead label={t("insights.campaigns.contribution")} sortKey="contribution" sort={sort} onSort={toggleSort} align="right" /> : null}
              <TableHead><span className="sr-only">{t("insights.common.actions")}</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {campaigns.map((campaign) => (
              <TableRow key={campaign.id} className="h-[82px] hover:bg-muted/25">
                <TableCell className="min-w-[250px] py-4">
                  <p className="font-medium text-foreground">{localize(campaign.name)}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <EvidenceTag tone={campaign.delivery === "Active" ? "active" : "neutral"}>{localize(campaign.delivery)}</EvidenceTag>
                    <EvidenceTag tone="info">{localize(campaign.channel)}</EvidenceTag>
                    <EvidenceTag tone="neutral">{localize(campaign.format)}</EvidenceTag>
                  </div>
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">{formatMoney(campaign.spend)}</TableCell>
                <TableCell className="min-w-[160px] text-right"><p className="text-sm font-semibold tabular-nums text-foreground">{t("insights.campaigns.attendedCount", { count: formatNumber(campaign.attended) })}</p><p className="mt-0.5 text-xs text-muted-foreground">{t("insights.campaigns.showOfBooked", { rate: formatNumber(campaign.showRate), count: formatNumber(campaign.booked) })}</p><ResultBar value={campaign.showRate} /></TableCell>
                <TableCell className="text-right text-sm tabular-nums">{campaign.costPerAttended === null ? t("insights.campaigns.noResult") : formatMoney(Math.round(campaign.costPerAttended))}</TableCell>
                <TableCell className="text-right text-sm font-medium tabular-nums text-foreground">{formatMoney(campaign.value)}</TableCell>
                <TableCell className="text-right"><p className="text-sm font-semibold tabular-nums text-foreground">{formatNumber(campaign.revenueReturn, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x</p>{campaign.revenueReturn === bestReturn ? <EvidenceTag tone="success" className="mt-1">{t("insights.campaigns.bestReturn")}</EvidenceTag> : null}</TableCell>
                {margin !== null ? <TableCell className={`text-right text-sm font-semibold tabular-nums ${campaign.contribution >= 0 ? "text-success" : "text-destructive"}`}>{campaign.contribution >= 0 ? formatMoney(Math.round(campaign.contribution)) : `-${formatMoney(Math.round(Math.abs(campaign.contribution)))}`}</TableCell> : null}
                <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => onOpenCampaign(campaign)} aria-label={t("insights.campaigns.viewDetailsAria", { name: localize(campaign.name) })}><Eye className="h-4 w-4" /> {t("insights.action.view")}</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DashboardCard>
  );
}

function ReportToolbar({ report }) {
  const { t, localize } = useI18n();
  return (
    <div className="mt-6 flex flex-col gap-3 rounded-xl border border-border bg-surface px-4 py-3 shadow-xs sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <EvidenceTag tone="info">{t("insights.report.sampleData")}</EvidenceTag>
        <span className="inline-flex items-center gap-1.5 font-medium text-foreground"><CalendarDays className="h-3.5 w-3.5" />{localize(report.range)}</span>
        <EvidenceTag tone={report.status === "Closed period" ? "success" : "neutral"}>{localize(report.status)}</EvidenceTag>
        <span className="hidden md:inline">{t("insights.report.comparedWith", { period: localize(report.comparison) })}</span>
      </div>
      <details className="group relative text-xs text-muted-foreground">
        <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-md px-2 py-1 font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <Info className="h-3.5 w-3.5" /> {t("insights.report.dataNotes")}
        </summary>
        <div className="mt-3 grid gap-3 border-t border-border pt-3 sm:absolute sm:right-0 sm:top-7 sm:z-20 sm:w-[520px] sm:rounded-xl sm:border sm:bg-surface sm:p-5 sm:shadow-pop">
          <p className="flex items-center gap-2"><EvidenceTag source="observed" /> {t("insights.report.observedNote")}</p>
          <p className="flex items-center gap-2"><EvidenceTag source="staff" /> {t("insights.report.enteredNote")}</p>
          <p className="flex items-center gap-2"><EvidenceTag source="derived" /> {t("insights.report.derivedNote")}</p>
          <p className="flex items-center gap-2"><EvidenceTag source="estimate" /> {t("insights.report.estimateNote")}</p>
        </div>
      </details>
    </div>
  );
}

/* Legacy one-column insight components retained temporarily for reference.
function ExecutiveMetrics({ report, showRate, valueChange, reduceMotion, onOpenMetric, onOpenInbox }) {
  const bookingRate = percent(report.bookings, report.enquiries);
  const metricTrend = report.metricTrend || [
    { label: report.range, showRate, bookingRate },
  ];
  return (
    <section aria-labelledby="executive-metrics-title">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 id="executive-metrics-title" className="text-lg font-semibold text-foreground">Commercial performance</h2>
          <p className="mt-1 text-sm text-muted-foreground">The three numbers that explain demand, conversion, and realized visit value.</p>
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <KpiCard
          label="Attended visit value"
          value={money(report.visitValue)}
          detail={`List-price value from ${report.attended} attended visits. Not collected cash.`}
          tag={<EvidenceTag source="derived" />}
          delta={valueChange === null ? null : `${valueChange >= 0 ? "+" : ""}${valueChange}% vs comparison`}
          deltaTone={valueChange >= 0 ? "success" : "destructive"}
          data={report.trend}
          dataKey="current"
          tone="primary"
          reduceMotion={reduceMotion}
          index={0}
          onClick={() => onOpenMetric("value")}
        />
        <KpiCard
          label="Show-up rate"
          value={`${showRate}%`}
          detail={`${report.attended} of ${report.dueBookings} due appointments attended.`}
          tag={<EvidenceTag source="observed" />}
          data={metricTrend}
          dataKey="showRate"
          tone="success"
          reduceMotion={reduceMotion}
          index={1}
          onClick={() => onOpenMetric("attended")}
        />
        <KpiCard
          label="Enquiry to booking"
          value={`${bookingRate}%`}
          detail={`${report.bookings} bookings created from ${report.enquiries} enquiries.`}
          tag={<EvidenceTag source="observed" />}
          data={metricTrend}
          dataKey="bookingRate"
          tone="human"
          reduceMotion={reduceMotion}
          index={2}
          onClick={onOpenInbox}
        />
      </div>
    </section>
  );
}

function KpiCard({ label, value, detail, tag, delta, deltaTone = "neutral", data, dataKey, tone, reduceMotion, index, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group min-w-0 rounded-xl border border-border bg-surface p-5 text-left shadow-xs transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-6"
      aria-label={`${label}: ${value}. ${detail} Open details.`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        {tag}
      </div>
      <div className="mt-5 flex flex-wrap items-end gap-3">
        <p className="text-[34px] font-semibold leading-none tracking-[-0.04em] text-foreground">{value}</p>
        {delta ? <EvidenceTag tone={deltaTone} className="mb-0.5">{delta}</EvidenceTag> : null}
      </div>
      <p className="mt-2 min-h-10 text-sm leading-5 text-muted-foreground">{detail}</p>
      <MetricSparkline data={data} dataKey={dataKey} tone={tone} reduceMotion={reduceMotion} index={index} />
    </button>
  );
}

function MetricSparkline({ data, dataKey, tone = "primary", reduceMotion, index = 0 }) {
  return (
    <div aria-hidden="true" className="pointer-events-none mt-4 h-16 min-w-0">
      <ResponsiveContainer
        width="100%"
        height="100%"
        minWidth={0}
        initialDimension={{ width: 280, height: 64 }}
      >
        <LineChart data={data} accessibilityLayer={false} margin={{ top: 6, right: 3, bottom: 2, left: 3 }}>
          <Line
            key={`${dataKey}-${data.length}`}
            type="monotone"
            dataKey={dataKey}
            dot={false}
            activeDot={false}
            stroke={`hsl(var(--${tone}))`}
            strokeWidth={2.25}
            isAnimationActive={!reduceMotion}
            animationBegin={index * 80}
            animationDuration={560}
            animationEasing="ease-out"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ValueTrendPanel({ report, showRate, noShows, reduceMotion, onOpenMetric, onOpenInbox }) {
  const bookingRate = percent(report.bookings, report.enquiries);
  return (
    <section aria-labelledby="value-trend-title" className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
      <div className="p-5 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="value-trend-title" className="text-lg font-semibold text-foreground">Attended value over time</h2>
              <EvidenceTag source="derived" />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Cumulative list-price value created by visits marked attended.</p>
          </div>
          <button type="button" onClick={() => onOpenMetric("value")} className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            View records <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <figure className="relative mt-7 min-w-0">
          <figcaption className="sr-only">This period ends at {money(report.visitValue)} in attended visit value, compared with {money(report.previousVisitValue)}.</figcaption>
          <div className="h-[290px] min-w-0">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={0}
              initialDimension={{ width: 900, height: 290 }}
            >
              <AreaChart accessibilityLayer data={report.trend} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`visitValueFill-${report.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.16} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.68} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} dy={10} />
                <YAxis hide domain={[0, "auto"]} tickFormatter={compactMoney} />
                <Tooltip isAnimationActive={!reduceMotion} content={<ValueChartTooltip comparison={report.comparison} />} cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "4 4" }} />
                <Area type="monotone" dataKey="previous" name="Comparison" stroke="hsl(var(--muted-foreground))" strokeWidth={2} strokeDasharray="5 5" fillOpacity={0} isAnimationActive={false} />
                <Area key={`${report.id}-current`} type="monotone" dataKey="current" name="This period" stroke="hsl(var(--primary))" strokeWidth={2.5} fill={`url(#visitValueFill-${report.id})`} isAnimationActive={!reduceMotion} animationDuration={700} animationEasing="ease-out" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2"><span className="h-0.5 w-5 bg-primary" />This period</span>
            <span className="inline-flex items-center gap-2"><span className="h-0.5 w-5 border-t-2 border-dashed border-muted-foreground/70" />{report.comparison}</span>
          </div>
        </figure>
      </div>

      <div className="grid border-t border-border bg-muted/30 sm:grid-cols-2 lg:grid-cols-4">
        <ConversionStep label="Enquiries" value={report.enquiries} detail={`${report.previousEnquiries} in comparison`} onClick={onOpenInbox} />
        <ConversionStep label="Bookings created" value={report.bookings} detail={`${bookingRate}% of enquiries`} onClick={() => onOpenMetric("bookings")} />
        <ConversionStep label="Visits attended" value={report.attended} detail={`${showRate}% of appointments due`} onClick={() => onOpenMetric("attended")} />
        <ConversionStep label="No-shows recovered" value={report.rebooked} detail={`${noShows} no-shows in the period`} onClick={() => onOpenMetric("noshow")} />
      </div>
    </section>
  );
}

*/
function ValueChartTooltip({ active, payload, label, comparison }) {
  const { t, formatMoney } = useI18n();
  if (!active || !payload?.length) return null;
  const current = payload.find((item) => item.dataKey === "current")?.value ?? 0;
  const previous = payload.find((item) => item.dataKey === "previous")?.value ?? 0;
  return (
    <div className="min-w-[190px] rounded-lg border border-border bg-surface p-3 text-xs shadow-pop">
      <p className="font-semibold text-foreground">{label}</p>
      <div className="mt-2 space-y-1.5 text-muted-foreground">
        <p className="flex items-center justify-between gap-4"><span>{t("insights.chart.thisPeriod")}</span><span className="font-semibold text-foreground">{formatMoney(current)}</span></p>
        <p className="flex items-center justify-between gap-4"><span className="max-w-[130px] break-words">{comparison}</span><span>{formatMoney(previous)}</span></p>
      </div>
    </div>
  );
}

/* Legacy one-column insight components retained temporarily for reference.
function ConversionStep({ label, value, detail, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex min-w-0 items-center justify-between gap-3 border-b border-border px-5 py-4 text-left transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:border-r sm:[&:nth-child(2)]:border-r-0 sm:[&:nth-child(3)]:border-b-0 sm:last:border-b-0 sm:last:border-r-0 lg:border-b-0 lg:[&:nth-child(2)]:border-r lg:px-6 lg:py-5">
      <div className="min-w-0">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tracking-tight text-foreground">{value}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{detail}</p>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-primary" />
    </button>
  );
}

function OpportunitiesPanel({ opportunities, largestOpportunity, onReview }) {
  return (
    <section aria-labelledby="opportunities-title">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="opportunities-title" className="text-lg font-semibold text-foreground">Recommended actions</h2>
            <EvidenceTag source="estimate" />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Modeled next steps, ranked after the observed reporting evidence above.</p>
        </div>
        <div className="sm:text-right">
          <p className="text-xs font-medium text-muted-foreground">Largest individual estimate</p>
          <p className="mt-1 text-xl font-semibold tracking-tight text-foreground">About {money(largestOpportunity)}</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
        <div className="divide-y divide-border">
        {opportunities.map((action) => {
          const Icon = action.icon;
          return (
            <div key={action.id} className="grid gap-4 px-5 py-5 md:grid-cols-[36px_minmax(0,1fr)_auto] md:items-center sm:px-6 lg:py-6">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/45 text-muted-foreground">
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-foreground">{action.title}</p>
                  <EvidenceTag source="estimate" />
                  <EvidenceTag tone="neutral">{action.confidence}</EvidenceTag>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{action.detail}</p>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-4 md:min-w-[275px] md:justify-end">
                <div className="md:text-right">
                  <p className="text-xs text-muted-foreground">Possible upside</p>
                  <p className="mt-0.5 text-xl font-semibold tracking-tight text-foreground">About {money(action.value)}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => onReview(action)}>
                  {action.cta} <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
        </div>
        <p className="border-t border-border bg-muted/25 px-5 py-3 text-xs text-muted-foreground sm:px-6">These scenarios may overlap. They are not added together and never count as observed attended visit value.</p>
      </div>
    </section>
  );
}

function CampaignEconomics({ campaigns, totals, report, trackedReturn, bookingCoverage, attendanceCoverage, margin, marginInput, setMarginInput, breakEvenAcquisition, sort, toggleSort, selectedMetric, setSelectedMetric, reduceMotion, onOpenCampaign }) {
  const costPerAttended = totals.attended ? totals.spend / totals.attended : 0;
  const values = {
    spend: totals.spend,
    attended: totals.attended,
    costPerAttended,
    campaignReturn: trackedReturn,
  };
  const metric = ACQUISITION_METRICS[selectedMetric];
  const bestReturn = Math.max(...campaigns.map((campaign) => campaign.revenueReturn));

  return (
    <section aria-labelledby="campaign-title">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="campaign-title" className="text-lg font-semibold text-foreground">Acquisition evidence</h2>
            <EvidenceTag tone="info">Attributed outcomes</EvidenceTag>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Measure campaign quality by attended visits, cost per result, and attended value, not message volume alone.</p>
        </div>
        <p className="text-xs text-muted-foreground">Select a metric to update the chart</p>
      </div>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        {Object.entries(ACQUISITION_METRICS).map(([key, item]) => (
          <button
            key={key}
            type="button"
            aria-pressed={selectedMetric === key}
            onClick={() => setSelectedMetric(key)}
            className={`rounded-xl border bg-surface p-4 text-left shadow-xs transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-5 ${selectedMetric === key ? "border-primary ring-1 ring-primary/15" : "border-border hover:border-primary/30"}`}
          >
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:justify-between">
              <p className="text-sm font-medium text-muted-foreground">{item.label}</p>
              <EvidenceTag source={item.source} />
            </div>
            <p className="mt-4 text-3xl font-semibold tracking-[-0.03em] text-foreground">{item.format(values[key])}</p>
            <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p>
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
        <div className="flex flex-col gap-2 border-b border-border px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-foreground">{metric.label} over the period</h3>
              <EvidenceTag source={metric.source} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{metric.detail}. The final point matches the campaign totals below.</p>
          </div>
          <p className="text-2xl font-semibold tracking-tight text-foreground">{metric.format(values[selectedMetric])}</p>
        </div>
        <figure className="relative min-w-0 px-4 pb-5 pt-4 sm:px-6 sm:pb-6">
          <figcaption className="sr-only">{metric.label} ends at {metric.format(values[selectedMetric])} for {report.range}.</figcaption>
          <div className="h-[240px] min-w-0">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={0}
              initialDimension={{ width: 900, height: 240 }}
            >
              <AreaChart accessibilityLayer data={report.acquisitionTrend} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`acquisitionFill-${report.id}-${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={`hsl(var(--${metric.tone}))`} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={`hsl(var(--${metric.tone}))`} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.7} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} dy={10} />
                <YAxis hide domain={[0, "auto"]} />
                <Tooltip isAnimationActive={!reduceMotion} content={<AcquisitionTooltip metric={metric} />} cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "4 4" }} />
                <Area
                  key={`${report.id}-${selectedMetric}`}
                  type="monotone"
                  dataKey={selectedMetric}
                  name={metric.label}
                  stroke={`hsl(var(--${metric.tone}))`}
                  strokeWidth={2.5}
                  fill={`url(#acquisitionFill-${report.id}-${selectedMetric})`}
                  isAnimationActive={!reduceMotion}
                  animationDuration={620}
                  animationEasing="ease-out"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </figure>

        <div className="grid gap-3 border-t border-border bg-muted/25 px-5 py-4 text-sm sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <EvidenceTag tone={bookingCoverage >= 80 ? "success" : "estimate"}>{bookingCoverage}% booking attribution</EvidenceTag>
            <EvidenceTag tone={attendanceCoverage >= 80 ? "success" : "estimate"}>{attendanceCoverage}% attended attribution</EvidenceTag>
            <span className="text-xs text-muted-foreground">Partial attribution means campaign totals will not equal the whole clinic report.</span>
          </div>
          <span className="text-xs font-medium text-muted-foreground">List-price value, not collected revenue</span>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
        <div className="flex flex-col gap-3 border-b border-border px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h3 className="text-base font-semibold text-foreground">Campaign results</h3>
            <p className="mt-1 text-sm text-muted-foreground">Delivery context beside the commercial outcome it produced.</p>
          </div>
          <details className="group sm:text-right">
            <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-sm font-semibold text-foreground shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Model contribution <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
            </summary>
            <div className="mt-3 flex items-center gap-2 sm:justify-end">
              <label htmlFor="gross-margin" className="text-sm text-muted-foreground">Gross margin</label>
              <Input
                id="gross-margin"
                type="number"
                min="1"
                max="100"
                inputMode="numeric"
                value={marginInput}
                onChange={(event) => setMarginInput(event.target.value === "" ? "" : String(Math.min(100, Math.max(1, Number(event.target.value))))) }
                placeholder="40"
                className="h-9 w-20 bg-surface text-right tabular-nums"
              />
              <span className="text-sm font-medium text-foreground">%</span>
            </div>
            <p className="mt-2 max-w-md text-xs text-muted-foreground">
              {breakEvenAcquisition === null
                ? "Optional. Enter margin to reveal contribution after campaign spend."
                : `At ${margin}% margin, first-visit acquisition breaks even near ${money(breakEvenAcquisition)}.`}
            </p>
          </details>
        </div>

        <div className="divide-y divide-border lg:hidden">
          {campaigns.map((campaign) => (
            <CampaignEvidenceCard key={campaign.id} campaign={campaign} margin={margin} bestReturn={bestReturn} onOpen={() => onOpenCampaign(campaign)} />
          ))}
        </div>

        <div className="hidden overflow-x-auto scroll-thin lg:block">
          <Table className="min-w-[940px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <SortableHead label="Campaign" sortKey="name" sort={sort} onSort={toggleSort} />
                <SortableHead label="Spend" sortKey="spend" sort={sort} onSort={toggleSort} align="right" />
                <SortableHead label="Attended result" sortKey="showRate" sort={sort} onSort={toggleSort} align="right" />
                <SortableHead label="Cost per attended" sortKey="costPerAttended" sort={sort} onSort={toggleSort} align="right" />
                <SortableHead label="Attended value" sortKey="value" sort={sort} onSort={toggleSort} align="right" />
                <SortableHead label="Value per ₾1" sortKey="revenueReturn" sort={sort} onSort={toggleSort} align="right" />
                {margin !== null ? <SortableHead label="Contribution" sortKey="contribution" sort={sort} onSort={toggleSort} align="right" /> : null}
                <TableHead className="relative"><span className="sr-only">Actions</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id} className="h-[84px] hover:bg-muted/25">
                  <TableCell className="min-w-[260px] py-4">
                    <p className="font-medium text-foreground">{campaign.name}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <EvidenceTag tone={campaign.delivery === "Active" ? "active" : "neutral"}>{campaign.delivery}</EvidenceTag>
                      <EvidenceTag tone="info">{campaign.channel}</EvidenceTag>
                      <EvidenceTag tone="neutral">{campaign.format}</EvidenceTag>
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{money(campaign.spend)}</TableCell>
                  <TableCell className="min-w-[148px] text-right">
                    <p className="text-sm font-semibold tabular-nums text-foreground">{campaign.attended} attended</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{campaign.showRate}% of {campaign.booked} booked</p>
                    <ResultBar value={campaign.showRate} />
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{campaign.costPerAttended === null ? "No result" : money(Math.round(campaign.costPerAttended))}</TableCell>
                  <TableCell className="text-right text-sm font-medium tabular-nums text-foreground">{money(campaign.value)}</TableCell>
                  <TableCell className="text-right">
                    <p className="text-sm font-semibold tabular-nums text-foreground">{campaign.revenueReturn.toFixed(1)}x</p>
                    {campaign.revenueReturn === bestReturn ? <EvidenceTag tone="success" className="mt-1">Best return</EvidenceTag> : null}
                  </TableCell>
                  {margin !== null ? (
                    <TableCell className={`text-right text-sm font-semibold tabular-nums ${campaign.contribution >= 0 ? "text-success" : "text-destructive"}`}>
                      {campaign.contribution >= 0 ? money(Math.round(campaign.contribution)) : `-${money(Math.round(Math.abs(campaign.contribution)))}`}
                    </TableCell>
                  ) : null}
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => onOpenCampaign(campaign)} aria-label={`View details for ${campaign.name}`}><Eye className="h-4 w-4" /> View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col gap-1 border-t border-border bg-muted/25 px-5 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span>{totals.booked} of {report.bookings} bookings are attributed to the tracked campaigns.</span>
          <span>Messages and message cost are available in campaign details.</span>
        </div>
      </div>
    </section>
  );
}

*/
function AcquisitionTooltip({ active, payload, label, metric }) {
  const { t, formatMoney, formatNumber } = useI18n();
  if (!active || !payload?.length) return null;
  return (
    <div className="min-w-[180px] rounded-lg border border-border bg-surface p-3 text-xs shadow-pop">
      <p className="font-semibold text-foreground">{label}</p>
      <p className="mt-2 flex items-center justify-between gap-4 text-muted-foreground"><span>{t(metric.labelKey)}</span><span className="font-semibold text-foreground">{formatAcquisitionMetric(metric, payload[0]?.value, formatMoney, formatNumber)}</span></p>
    </div>
  );
}

function ResultBar({ value }) {
  return (
    <span className="mt-2 ml-auto block h-1.5 w-24 overflow-hidden rounded-full bg-muted" aria-hidden="true">
      <span className="block h-full rounded-full bg-success transition-[width] duration-500" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </span>
  );
}

function CampaignEvidenceCard({ campaign, margin, bestReturn, onOpen }) {
  const { t, localize, formatMoney, formatNumber } = useI18n();
  return (
    <article className="p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold text-foreground">{localize(campaign.name)}</h4>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <EvidenceTag tone={campaign.delivery === "Active" ? "active" : "neutral"}>{localize(campaign.delivery)}</EvidenceTag>
            <EvidenceTag tone="info">{localize(campaign.channel)}</EvidenceTag>
            <EvidenceTag tone="neutral">{localize(campaign.format)}</EvidenceTag>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onOpen}><Eye className="h-4 w-4" /> {t("insights.action.view")}</Button>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-4">
        <CampaignCardMetric label={t("insights.campaigns.spend")} value={formatMoney(campaign.spend)} />
        <CampaignCardMetric label={t("insights.campaigns.attended")} value={`${formatNumber(campaign.attended)} · ${formatNumber(campaign.showRate)}%`} />
        <CampaignCardMetric label={t("insights.campaigns.costPerAttendedShort")} value={campaign.costPerAttended === null ? t("insights.campaigns.noResult") : formatMoney(Math.round(campaign.costPerAttended))} />
        <CampaignCardMetric label={t("insights.campaigns.valuePerLariShort")} value={`${formatNumber(campaign.revenueReturn, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x`} tag={campaign.revenueReturn === bestReturn ? t("insights.campaigns.bestReturn") : null} />
        {margin !== null ? <CampaignCardMetric label={t("insights.campaigns.contribution")} value={campaign.contribution >= 0 ? formatMoney(Math.round(campaign.contribution)) : `-${formatMoney(Math.round(Math.abs(campaign.contribution)))}`} /> : null}
      </div>
      <ResultBar value={campaign.showRate} />
    </article>
  );
}

function CampaignCardMetric({ label, value, tag }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-semibold tabular-nums text-foreground">{value}</p>
      {tag ? <EvidenceTag tone="success" className="mt-1">{tag}</EvidenceTag> : null}
    </div>
  );
}

function DecisionAndContext({ openPipeline, staleCount, activeRows, activeSort, setActiveSort, onOpenAttended, nav }) {
  const { t, localize, formatMoney, formatNumber } = useI18n();
  const decisionShowRate = percent(LAST_DECISION.attended, LAST_DECISION.bookings);
  const sortedRows = [...activeRows].sort((a, b) => activeSort === "wait"
    ? b.waitMinutes - a.waitMinutes || b.potentialValue - a.potentialValue
    : b.potentialValue - a.potentialValue || b.waitMinutes - a.waitMinutes);
  const visibleRows = sortedRows.slice(0, 5);
  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(380px,0.82fr)_minmax(0,1.38fr)]">
      <section aria-labelledby="decision-title" className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
        <div className="border-b border-border px-5 py-5 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 id="decision-title" className="text-base font-semibold text-foreground">{t("insights.decision.title")}</h2>
              <p className="mt-1 text-xs text-muted-foreground">{t("insights.decision.measuredAfter", { date: localize(LAST_DECISION.date) })}</p>
            </div>
            <EvidenceTag source="derived" />
          </div>
        </div>
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-primary/10 bg-primary-soft text-primary-soft-foreground"><CircleDollarSign className="h-5 w-5" /></span>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-5 text-foreground">{localize(LAST_DECISION.title)}</p>
              <p className="mt-2 text-3xl font-semibold tracking-[-0.03em] text-foreground">{formatMoney(LAST_DECISION.visitValue)}</p>
            </div>
          </div>
          <p className="mt-4 text-sm leading-6 text-muted-foreground">{t("insights.decision.valueDetail", { attended: formatNumber(LAST_DECISION.attended), bookings: formatNumber(LAST_DECISION.bookings) })}</p>
          <div className="mt-5 flex items-end justify-between gap-4 border-t border-border pt-4">
            <div><p className="text-xs text-muted-foreground">{t("insights.metric.showRate")}</p><p className="mt-1 text-xl font-semibold text-foreground">{formatNumber(decisionShowRate)}%</p></div>
            <button type="button" onClick={onOpenAttended} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{t("insights.action.reviewVisits")} <ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </section>

      <section aria-labelledby="active-customers-title" className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
        <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-soft-foreground"><MessageCircle className="h-4 w-4" /></span>
            <div><h2 id="active-customers-title" className="text-base font-semibold text-foreground">{t("insights.queue.title")}</h2><p className="mt-1 text-xs text-muted-foreground">{t("insights.queue.description")}</p></div>
          </div>
          <div className="inline-flex flex-wrap self-start rounded-lg border border-border bg-muted/35 p-1" role="group" aria-label={t("insights.queue.sortAria")}>
            <button type="button" aria-pressed={activeSort === "wait"} onClick={() => setActiveSort("wait")} className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${activeSort === "wait" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>{t("insights.queue.longestWait")}</button>
            <button type="button" aria-pressed={activeSort === "value"} onClick={() => setActiveSort("value")} className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${activeSort === "value" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>{t("insights.queue.highestValue")}</button>
          </div>
        </div>
        <div className="divide-y divide-border">
          {visibleRows.map((row) => {
            const waitingTone = row.statusCode === "Operator required" && row.waitMinutes >= 1440
              ? "text-destructive"
              : row.statusCode === "Operator required" && row.waitMinutes >= 60
                ? "text-warning"
                : "text-muted-foreground";
            return (
              <button key={row.conversationId} type="button" onClick={() => nav(`/inbox/${row.conversationId}`)} className="grid w-full grid-cols-[36px_minmax(0,1fr)] items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:grid-cols-[36px_minmax(0,1fr)_96px_88px] sm:px-6">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">{initials(row.name)}</span>
                <span className="min-w-0"><span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5"><span className="truncate text-sm font-semibold text-foreground">{row.name}</span><span className="text-[11px] text-muted-foreground">{row.channel}</span></span><span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{row.service} · {row.status}</span></span>
                <span className="col-start-2 text-left sm:col-auto sm:text-right"><span className={`block text-sm font-semibold tabular-nums ${waitingTone}`}>{row.waitLabel}</span><span className="text-[10px] text-muted-foreground">{t("insights.queue.sinceActivity")}</span></span>
                <span className="col-start-2 text-left sm:col-auto sm:text-right"><span className="inline-flex items-center justify-end gap-1 text-sm font-semibold tabular-nums text-foreground"><span className="h-1.5 w-1.5 rounded-full bg-destructive" />{formatMoney(row.potentialValue)}</span><span className="block text-[10px] text-muted-foreground">{t("insights.queue.potential")}</span></span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-col gap-2 border-t border-border bg-muted/25 px-5 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span>{t("insights.queue.footer", { value: formatMoney(openPipeline), count: formatNumber(staleCount) })}</span>
          <button type="button" onClick={() => nav("/inbox")} className="inline-flex items-center gap-1 font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{t("insights.queue.viewAll", { count: formatNumber(activeRows.length) })} <ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
      </section>
    </div>
  );
}

function SortableHead({ label, sortKey, sort, onSort, align = "left" }) {
  return (
    <TableHead className={align === "right" ? "text-right" : "text-left"} aria-sort={sort.key === sortKey ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(sortKey)} className={`inline-flex items-center gap-1 whitespace-nowrap text-xs hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${align === "right" ? "justify-end" : "justify-start"}`}>
        {label}<ArrowUpDown className="h-3 w-3" />
      </button>
    </TableHead>
  );
}

function InsightDialog({ drill, setDrill, state, nav, report }) {
  const { t, localize, formatMoney, formatNumber } = useI18n();
  const close = () => setDrill(null);
  const sampleFollowups = state.customers.filter((customer) => ["New", "Qualifying", "Qualified"].includes(customer.stage));
  const sampleNoShows = state.bookings.filter((booking) => booking.state === "noshow");

  return (
    <Dialog open={!!drill} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-h-[84vh] max-w-2xl overflow-y-auto scroll-thin">
        {drill?.type === "metric" ? (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2 pr-8"><DialogTitle>{drill.label}</DialogTitle><EvidenceTag source={drill.source} /></div>
              <DialogDescription>{drill.description}</DialogDescription>
            </DialogHeader>
            <PrototypeRecordNote report={report} />
            <BookingRecords records={drill.records} state={state} nav={nav} close={close} period={report.range} />
          </>
        ) : null}

        {drill?.type === "opportunity" ? (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2 pr-8"><DialogTitle>{drill.action.title}</DialogTitle><EvidenceTag source="estimate" /></div>
              <DialogDescription>{t("insights.dialog.reviewAssumptions")}</DialogDescription>
            </DialogHeader>
            <div className="rounded-xl border border-warning/30 bg-warning-soft p-4">
              <p className="text-xs font-medium text-warning-soft-foreground">{t("insights.dialog.estimatedAdditionalValue")}</p>
              <p className="mt-1 text-2xl font-semibold tracking-tight text-foreground">{t("insights.value.about", { value: formatMoney(drill.action.value) })}</p>
              <p className="mt-2 text-xs font-medium text-muted-foreground">{drill.action.formula}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t("insights.dialog.notBookedOrCollected", { confidence: drill.action.confidence })}</p>
            </div>
            {drill.action.id === "reallocate" ? (
              <div className="space-y-2">
                {drill.action.campaigns.map((campaign) => (
                  <div key={campaign.id} className="flex items-center justify-between rounded-lg border border-border bg-background p-3">
                    <div><p className="text-sm font-medium text-foreground">{localize(campaign.name)}</p><p className="text-xs text-muted-foreground">{t("insights.dialog.attendedFromBookings", { attended: formatNumber(campaign.attended), bookings: formatNumber(campaign.booked) })}</p></div>
                    <p className="font-sans text-sm font-semibold text-foreground tabular-nums">{formatNumber(campaign.revenueReturn, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x</p>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <PrototypeRecordNote report={report} />
                {drill.action.id === "noshow"
                  ? <BookingRecords records={sampleNoShows} state={state} nav={nav} close={close} period={report.range} />
                  : <CustomerRecords records={sampleFollowups} nav={nav} close={close} period={report.range} />}
              </>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={close}>{t("a11y.close")}</Button>
              {drill.action.route ? <Button onClick={() => { nav(drill.action.route); close(); }}>{drill.action.cta}<ArrowRight className="h-4 w-4" /></Button> : null}
            </DialogFooter>
          </>
        ) : null}

        {drill?.type === "campaign" ? (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2 pr-8"><DialogTitle>{localize(drill.campaign.name)}</DialogTitle><EvidenceTag source="derived" /></div>
              <DialogDescription>{t("insights.dialog.campaignDescription")}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap gap-2">
              <EvidenceTag tone={drill.campaign.delivery === "Active" ? "active" : "neutral"}>{localize(drill.campaign.delivery)}</EvidenceTag>
              <EvidenceTag tone="info">{localize(drill.campaign.channel)}</EvidenceTag>
              <EvidenceTag tone="neutral">{localize(drill.campaign.format)}</EvidenceTag>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <DialogMetric label={t("insights.campaigns.spend")} value={formatMoney(drill.campaign.spend)} />
              <DialogMetric label={t("insights.campaigns.booked")} value={formatNumber(drill.campaign.booked)} />
              <DialogMetric label={t("insights.campaigns.attended")} value={formatNumber(drill.campaign.attended)} />
              <DialogMetric label={t("insights.campaigns.valuePerLariSpent")} value={`${formatNumber(drill.campaign.revenueReturn, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x`} />
            </div>
            <div className="rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground">
              <p><span className="font-medium text-foreground">{t("insights.dialog.conversationResults")}</span> {t("insights.dialog.attributedMessages", { count: formatNumber(drill.campaign.enquiries), cost: formatMoney(drill.campaign.messageCost.toFixed(2)) })}</p>
              <p className="mt-2"><span className="font-medium text-foreground">{t("insights.dialog.showUp")}</span> {t("insights.dialog.showUpDetail", { attended: formatNumber(drill.campaign.attended), booked: formatNumber(drill.campaign.booked), rate: formatNumber(drill.campaign.showRate) })}</p>
              <p className="mt-2"><span className="font-medium text-foreground">{t("insights.dialog.costPerAttended")}</span> {drill.campaign.costPerAttended === null ? t("insights.dialog.noAttendanceYet") : formatMoney(Math.round(drill.campaign.costPerAttended))}.</p>
              <p className="mt-2"><span className="font-medium text-foreground">{t("insights.dialog.attendedValue")}</span> {t("insights.dialog.atListPrice", { value: formatMoney(drill.campaign.value) })}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>{t("a11y.close")}</Button>
              <Button onClick={() => { nav("/settings/channels"); close(); }}>{t("insights.action.openChannelSettings")}<ArrowRight className="h-4 w-4" /></Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function PrototypeRecordNote({ report }) {
  const { t, localize } = useI18n();
  return (
    <div className="flex items-start gap-2 rounded-lg border border-border bg-background p-3 text-xs leading-relaxed text-muted-foreground">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
      <span><strong className="font-medium text-foreground">{t("insights.records.period", { period: localize(report.range) })}</strong> {t("insights.records.prototypeNote")}</span>
    </div>
  );
}

function BookingRecords({ records, state, nav, close, period }) {
  const { t, localize, formatDate, formatMoney } = useI18n();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const normalized = query.trim().toLowerCase();
  const filtered = records.filter((booking) => {
    const customer = state.customers.find((item) => item.id === booking.customerId);
    const service = state.services.find((item) => item.id === booking.serviceId);
    return !normalized || `${customer?.name || ""} ${service?.name || ""} ${booking.state}`.toLowerCase().includes(normalized);
  });
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  if (!records.length) return <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("insights.records.noOperational")}</p>;
  return (
    <div className="space-y-3">
      <RecordListControls period={period} query={query} onQuery={(value) => { setQuery(value); setPage(1); }} total={filtered.length} />
      <div className="space-y-2">
      {visible.map((booking) => {
        const customer = state.customers.find((item) => item.id === booking.customerId);
        const service = state.services.find((item) => item.id === booking.serviceId);
        return (
          <button key={booking.id} type="button" onClick={() => { nav(`/customers/${booking.customerId}`); close(); }} className="flex w-full items-center gap-3 rounded-lg border border-border bg-background p-3 text-left hover:border-ring/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">{initials(customer?.name)}</span>
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-foreground">{customer?.name}</p><p className="text-xs leading-5 text-muted-foreground">{localize(service?.name)} · {formatDate(booking.start, { day: "2-digit", month: "short", year: "numeric" })} · {formatMoney(booking.price)}</p></div>
            <BookingStateBadge state={booking.state} />
          </button>
        );
      })}
      {!visible.length ? <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("insights.records.noSearchMatch")}</p> : null}
      </div>
      <RecordPager page={currentPage} pageCount={pageCount} onPage={setPage} total={filtered.length} />
    </div>
  );
}

function CustomerRecords({ records, nav, close, period }) {
  const { t, localize, formatMoney } = useI18n();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const normalized = query.trim().toLowerCase();
  const filtered = records.filter((customer) => !normalized || `${customer.name} ${customer.qualification.map((field) => field.value).join(" ")}`.toLowerCase().includes(normalized));
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  if (!records.length) return <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("insights.records.noOperational")}</p>;
  return (
    <div className="space-y-3">
      <RecordListControls period={period} query={query} onQuery={(value) => { setQuery(value); setPage(1); }} total={filtered.length} />
      <div className="space-y-2">
      {visible.map((customer) => (
        <button key={customer.id} type="button" onClick={() => { nav(`/customers/${customer.id}`); close(); }} className="flex w-full items-center gap-3 rounded-lg border border-border bg-background p-3 text-left hover:border-ring/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">{initials(customer.name)}</span>
          <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-foreground">{customer.name}</p><p className="text-xs leading-5 text-muted-foreground">{localize(customer.qualification[0]?.value)} · {t("insights.records.enteredValue", { value: formatMoney(customer.value) })}</p></div>
          <StageBadge stage={customer.stage} />
        </button>
      ))}
      {!visible.length ? <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("insights.records.noCustomersMatch")}</p> : null}
      </div>
      <RecordPager page={currentPage} pageCount={pageCount} onPage={setPage} total={filtered.length} />
    </div>
  );
}

function RecordListControls({ period, query, onQuery, total }) {
  const { t, localize, formatNumber } = useI18n();
  return (
    <div className="grid gap-3 rounded-lg border border-border bg-muted/35 p-3 sm:grid-cols-[1fr_220px]">
      <div>
        <p className="text-[11px] font-medium text-muted-foreground">{t("insights.page.reportingPeriod")}</p>
        <p className="mt-1 text-sm font-medium text-foreground">{localize(period)}</p>
      </div>
      <div>
        <label htmlFor="record-search" className="text-[11px] font-medium text-muted-foreground">{t("insights.records.searchCount", { count: formatNumber(total) })}</label>
        <Input id="record-search" value={query} onChange={(event) => onQuery(event.target.value)} placeholder={t("insights.records.searchPlaceholder")} className="mt-1 h-9 bg-surface" />
      </div>
    </div>
  );
}

function RecordPager({ page, pageCount, onPage, total }) {
  const { t, formatNumber } = useI18n();
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
      <p className="text-xs text-muted-foreground">{t("insights.records.matching", { count: formatNumber(total) })}</p>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>{t("pagination.previous")}</Button>
        <span className="min-w-16 text-center text-xs text-muted-foreground">{t("insights.pagination.page", { page: formatNumber(page), count: formatNumber(pageCount) })}</span>
        <Button size="sm" variant="outline" disabled={page >= pageCount} onClick={() => onPage(page + 1)}>{t("pagination.next")}</Button>
      </div>
    </div>
  );
}

function DialogMetric({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}
